/**
 * Procedural plant construction.
 *
 * Every species is built in code rather than loaded as a mesh. That keeps the
 * whole payload in the tens of kilobytes — which matters a great deal when the
 * thing is opened from a link on a phone, on cellular data.
 *
 * Each builder returns a `PlantModel` whose `setGrowth(t)` drives the entire
 * silhouette from sprout to mature. Growth is continuous, not stepped, so the
 * game can tween between stages instead of popping.
 *
 * ## Why every repeated part is instanced
 *
 * A plant is mostly *the same shape several times over*: seven canopy blobs,
 * ten blossoms, eleven flower spikes, eight fronds. Built as individual
 * meshes, a full plot of twelve cost 378 draw calls on its own — 90% of the
 * frame, before the plot, the architecture or the desert. Mid-range Android
 * running a camera feed underneath does not have that to spare.
 *
 * So each of those loops is one `InstancedMesh`. The scene graph above them is
 * untouched: the growth tweens still scale and hide the `canopy`, `blossoms`
 * and `spikes` groups exactly as before, because an instanced mesh sits inside
 * them like any other child. Only the parts that genuinely differ — a trunk, a
 * caudex — stay as their own mesh.
 */

import * as THREE from 'three';
import type { Species } from '../content/species';
import type { Mutation } from '../content/weather';

/** Shared low-poly sphere. Instanced, never mutated in place. */
const BLOB_GEO = new THREE.IcosahedronGeometry(1, 0);
const BLOOM_GEO = new THREE.IcosahedronGeometry(1, 0);

export interface PlantModel {
  /** Root object to add to the scene. Sits on its own origin at ground level. */
  root: THREE.Group;
  /** 0 = just sprouted, 1 = fully mature. */
  setGrowth(t: number): void;
  /** World-space point the burst VFX and score popups should fire from. */
  crownHeight(): number;
  /** Per-frame idle motion. `time` is seconds, `sway` scales wind strength. */
  update(time: number, sway: number): void;
  /** Mark the specimen with what the weather did to it. Null clears it. */
  setMutation(mutation: Mutation | null): void;
  dispose(): void;
}

/**
 * What a form builder returns. Mutation tinting is identical across all four
 * silhouettes, so `withMutation` adds it once rather than each builder
 * reimplementing it.
 */
type BuiltPlant = Omit<PlantModel, 'setMutation'>;

/** Deterministic PRNG so a given plant looks the same every time it loads. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/* ------------------------------------------------------------------ *
 * Instancing helpers
 * ------------------------------------------------------------------ */

/**
 * Scratch transform used to compose instance matrices. Never added to a
 * scene, never held across a call — `pose` is synchronous and finishes with
 * the matrix before anything else can touch it.
 */
const SCRATCH = new THREE.Object3D();

/** Reset the scratch and hand it to the caller to pose. */
function posed(place: (o: THREE.Object3D) => void): THREE.Matrix4 {
  SCRATCH.position.set(0, 0, 0);
  SCRATCH.rotation.set(0, 0, 0);
  SCRATCH.scale.set(1, 1, 1);
  place(SCRATCH);
  SCRATCH.updateMatrix();
  return SCRATCH.matrix;
}

/**
 * One draw call for `count` copies of the same geometry.
 *
 * `place` is invoked once per instance **in index order**, which matters more
 * than it looks: the builders draw their jitter from a seeded PRNG, so the
 * order of `rand()` calls is part of what a given plant looks like. Keeping
 * the loop shape identical to the per-mesh version it replaced is what makes
 * this change invisible on screen.
 */
function instanced(
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  count: number,
  place: (o: THREE.Object3D, i: number) => void,
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geo, mat, count);
  for (let i = 0; i < count; i++) {
    mesh.setMatrixAt(i, posed((o) => place(o, i)));
  }
  mesh.instanceMatrix.needsUpdate = true;
  mesh.castShadow = true;
  /*
   * Frustum culling an InstancedMesh means maintaining a bounding volume that
   * covers every instance, and the wind re-poses fronds and spikes every
   * frame. The whole plot is barely a metre across and is always the subject
   * of the shot, so culling these saves nothing — and a stale bound pops a
   * plant out of view, which is the one failure that would actually be seen.
   */
  mesh.frustumCulled = false;
  return mesh;
}

/** Rewrite every instance matrix. For the parts the wind moves. */
function repose(
  mesh: THREE.InstancedMesh,
  place: (o: THREE.Object3D, i: number) => void,
): void {
  for (let i = 0; i < mesh.count; i++) {
    mesh.setMatrixAt(i, posed((o) => place(o, i)));
  }
  mesh.instanceMatrix.needsUpdate = true;
}

function leafMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.85,
    metalness: 0,
    flatShading: true,
  });
}

function stemMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.95,
    metalness: 0,
    flatShading: true,
  });
}

function bloomMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.55,
    metalness: 0,
    emissive: new THREE.Color(color).multiplyScalar(0.25),
    flatShading: true,
  });
}

/** Collect every material/geometry we own so dispose() is honest. */
class Disposer {
  private items: Array<{ dispose(): void }> = [];
  track<T extends { dispose(): void }>(item: T): T {
    this.items.push(item);
    return item;
  }
  disposeAll(): void {
    for (const item of this.items) item.dispose();
    this.items.length = 0;
  }
}

/* ------------------------------------------------------------------ *
 * Date palm
 * ------------------------------------------------------------------ */

/**
 * A single frond, built as a tapered ribbon that arcs outward and droops.
 * Leaflets are cut into the ribbon by alternating the vertex offsets, which
 * reads as pinnate at arm's length for a fraction of the triangles.
 *
 * Every frond on a palm is the same ribbon under a different pivot, so this
 * builds the geometry once and the crown instances it.
 */
function frondGeometry(length: number, width: number): THREE.BufferGeometry {
  const segments = 10;
  const positions: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= segments; i++) {
    const u = i / segments;
    // Arc up then over — the classic palm droop.
    const x = length * u;
    const y = Math.sin(u * Math.PI * 0.55) * length * 0.34 - u * u * length * 0.42;
    const z = 0;
    // Taper to a point, with a slight bulge at a third of the way out.
    const w = width * Math.sin(Math.PI * Math.min(1, u * 1.05)) * (1 - u * 0.35);
    // Alternate the leaflet edge so the silhouette is serrated, not smooth.
    const jag = i % 2 === 0 ? 1 : 0.62;
    positions.push(x, y, z - w * jag, x, y, z + w * jag);
  }

  for (let i = 0; i < segments; i++) {
    const a = i * 2;
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

function buildPalm(species: Species, seed: number): BuiltPlant {
  const d = new Disposer();
  const root = new THREE.Group();
  const rand = rng(seed);

  const leafMat = d.track(leafMaterial(species.leaf));
  const stemMat = d.track(stemMaterial(species.stem));
  const fruitMat = d.track(bloomMaterial(species.bloom));

  const H = species.matureHeight;

  // Trunk: a lathe so we get the characteristic taper and the stepped
  // leaf-scar texture of a real date palm for almost nothing.
  const profile: THREE.Vector2[] = [];
  const rings = 12;
  for (let i = 0; i <= rings; i++) {
    const u = i / rings;
    const step = 1 + Math.sin(u * Math.PI * 7) * 0.06; // leaf scars
    profile.push(new THREE.Vector2((0.05 - u * 0.018) * step * H * 1.4, u * H * 0.72));
  }
  const trunkGeo = d.track(new THREE.LatheGeometry(profile, 7));
  const trunk = new THREE.Mesh(trunkGeo, stemMat);
  trunk.castShadow = true;
  root.add(trunk);

  // Crown sits at the top of the trunk and holds every frond.
  const crown = new THREE.Group();
  crown.position.y = H * 0.72;
  root.add(crown);

  // Each frond's fixed pose. `scale` and `tilt` are rewritten by growth and
  // by the wind; the instance matrices are recomposed from all four.
  const FROND_COUNT = 8;
  const fronds: { yaw: number; droop: number; scale: number; tilt: number }[] = [];
  for (let i = 0; i < FROND_COUNT; i++) {
    const yaw = (i / FROND_COUNT) * Math.PI * 2 + rand() * 0.25;
    // Outer fronds sit lower; inner spear points nearly straight up.
    const droop = i < 2 ? -0.15 : 0.1 + (i / FROND_COUNT) * 0.5 + rand() * 0.12;
    fronds.push({ yaw, droop, scale: 0.001, tilt: 0 });
  }

  // Crown spans roughly 0.9x the palm's height — stylised, but tight enough
  // that neighbouring palms read as separate trees.
  const frondGeo = d.track(frondGeometry(H * 0.45, H * 0.058));
  const poseFrond = (o: THREE.Object3D, i: number) => {
    const f = fronds[i];
    o.rotation.set(f.tilt, f.yaw, -f.droop);
    o.scale.setScalar(f.scale);
  };
  const frondMesh = d.track(instanced(frondGeo, leafMat, FROND_COUNT, poseFrond));
  crown.add(frondMesh);

  // Date clusters — only revealed near full growth.
  const dates = new THREE.Group();
  const dateMesh = d.track(
    instanced(BLOB_GEO, fruitMat, 3, (o, i) => {
      const a = (i / 3) * Math.PI * 2;
      o.position.set(Math.cos(a) * H * 0.13, -H * 0.05, Math.sin(a) * H * 0.13);
      o.scale.setScalar(H * 0.07);
    }),
  );
  dateMesh.castShadow = false;
  dates.add(dateMesh);
  crown.add(dates);

  let growth = 0;

  return {
    root,
    setGrowth(t) {
      growth = THREE.MathUtils.clamp(t, 0, 1);
      // Trunk extends first, crown rides on top of it.
      const trunkScale = THREE.MathUtils.smoothstep(growth, 0, 0.85);
      trunk.scale.set(
        0.45 + trunkScale * 0.55,
        Math.max(0.04, trunkScale),
        0.45 + trunkScale * 0.55,
      );
      crown.position.y = H * 0.72 * Math.max(0.04, trunkScale);

      // Fronds unfurl one after another rather than all at once.
      fronds.forEach((f, i) => {
        const start = (i / FROND_COUNT) * 0.55;
        const local = THREE.MathUtils.clamp((growth - start) / 0.4, 0, 1);
        f.scale = 0.001 + THREE.MathUtils.smoothstep(local, 0, 1);
      });
      repose(frondMesh, poseFrond);

      const fruiting = THREE.MathUtils.clamp((growth - 0.82) / 0.18, 0, 1);
      dates.visible = fruiting > 0.01;
      dates.scale.setScalar(0.001 + fruiting);
    },
    crownHeight() {
      return H * 0.72 * Math.max(0.04, THREE.MathUtils.smoothstep(growth, 0, 0.85)) + H * 0.1;
    },
    update(time, sway) {
      fronds.forEach((f, i) => {
        f.tilt = Math.sin(time * 1.1 + i * 0.9) * 0.055 * sway;
      });
      repose(frondMesh, poseFrond);
      crown.rotation.y = Math.sin(time * 0.45) * 0.03 * sway;
    },
    dispose() {
      d.disposeAll();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Canopy trees — ghaf, acacia, olive
 * ------------------------------------------------------------------ */

function buildCanopy(species: Species, seed: number): BuiltPlant {
  const d = new Disposer();
  const root = new THREE.Group();
  const rand = rng(seed);

  const leafMat = d.track(leafMaterial(species.leaf));
  const stemMat = d.track(stemMaterial(species.stem));
  const bloomMat = d.track(bloomMaterial(species.bloom));

  const H = species.matureHeight;
  // The umbrella thorn holds its canopy flat and level; sidr is a dense
  // near-sphere; ghaf sits between the two.
  const flatness = species.id === 'acacia' ? 0.42 : species.id === 'sidr' ? 0.82 : 0.62;

  const trunkH = H * (species.id === 'acacia' ? 0.52 : 0.42);
  const trunkGeo = d.track(
    new THREE.CylinderGeometry(H * 0.028, H * 0.055, trunkH, 6, 1),
  );
  const trunk = new THREE.Mesh(trunkGeo, stemMat);
  trunk.position.y = trunkH / 2;
  trunk.castShadow = true;
  root.add(trunk);

  // A few branches lifting toward the canopy edge.
  const branchGroup = new THREE.Group();
  branchGroup.position.y = trunkH * 0.72;
  root.add(branchGroup);

  const BRANCHES = 4;
  const branchGeo = d.track(new THREE.CylinderGeometry(H * 0.012, H * 0.026, H * 0.34, 5, 1));
  branchGroup.add(
    d.track(
      instanced(branchGeo, stemMat, BRANCHES, (o, i) => {
        const a = (i / BRANCHES) * Math.PI * 2 + rand() * 0.5;
        const tilt = 0.55 + rand() * 0.25;
        o.position.set(Math.cos(a) * H * 0.07, H * 0.14, Math.sin(a) * H * 0.07);
        o.rotation.set(Math.sin(a) * tilt, 0, -Math.cos(a) * tilt);
      }),
    ),
  );

  // Canopy: overlapping blobs, squashed vertically to taste.
  const canopy = new THREE.Group();
  canopy.position.y = trunkH + H * 0.16 * flatness;
  root.add(canopy);

  const BLOBS = 7;
  canopy.add(
    d.track(
      instanced(BLOB_GEO, leafMat, BLOBS, (o, i) => {
        const a = (i / BLOBS) * Math.PI * 2 + rand();
        const r = i === 0 ? 0 : H * (0.14 + rand() * 0.13);
        o.position.set(Math.cos(a) * r, (rand() - 0.35) * H * 0.09, Math.sin(a) * r);
        const s = H * (0.15 + rand() * 0.08);
        o.scale.set(s, s * flatness, s);
        o.rotation.set(rand() * 3, rand() * 3, rand() * 3);
      }),
    ),
  );

  // Blossom flecks scattered through the canopy at maturity.
  const blossoms = new THREE.Group();
  const blossomMesh = d.track(
    instanced(BLOOM_GEO, bloomMat, 10, (o) => {
      const a = rand() * Math.PI * 2;
      const r = H * (0.05 + rand() * 0.24);
      o.position.set(Math.cos(a) * r, (rand() - 0.3) * H * 0.12, Math.sin(a) * r);
      o.scale.setScalar(H * 0.022);
    }),
  );
  blossomMesh.castShadow = false;
  blossoms.add(blossomMesh);
  canopy.add(blossoms);

  let growth = 0;

  return {
    root,
    setGrowth(t) {
      growth = THREE.MathUtils.clamp(t, 0, 1);
      const trunkT = Math.max(0.05, THREE.MathUtils.smoothstep(growth, 0, 0.7));
      trunk.scale.set(0.4 + trunkT * 0.6, trunkT, 0.4 + trunkT * 0.6);
      trunk.position.y = (trunkH * trunkT) / 2;

      branchGroup.position.y = trunkH * trunkT * 0.72;
      branchGroup.scale.setScalar(THREE.MathUtils.smoothstep(growth, 0.25, 0.8));
      branchGroup.visible = growth > 0.25;

      // Canopy lags the trunk, then swells — a sapling is a stick first.
      const canopyT = THREE.MathUtils.smoothstep(growth, 0.15, 1);
      canopy.position.y = trunkH * trunkT + H * 0.16 * flatness * canopyT;
      canopy.scale.setScalar(0.001 + canopyT);
      canopy.visible = canopyT > 0.01;

      const flowering = THREE.MathUtils.clamp((growth - 0.75) / 0.25, 0, 1);
      blossoms.visible = flowering > 0.01;
      blossoms.scale.setScalar(0.001 + flowering);
    },
    crownHeight() {
      const trunkT = Math.max(0.05, THREE.MathUtils.smoothstep(growth, 0, 0.7));
      return trunkH * trunkT + H * 0.2;
    },
    update(time, sway) {
      canopy.rotation.z = Math.sin(time * 0.9) * 0.035 * sway;
      canopy.rotation.x = Math.cos(time * 0.7) * 0.028 * sway;
    },
    dispose() {
      d.disposeAll();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Desert rose — a caudex succulent
 * ------------------------------------------------------------------ */

function buildSucculent(species: Species, seed: number): BuiltPlant {
  const d = new Disposer();
  const root = new THREE.Group();
  const rand = rng(seed);

  const leafMat = d.track(leafMaterial(species.leaf));
  const stemMat = d.track(stemMaterial(species.stem));
  const bloomMat = d.track(bloomMaterial(species.bloom));

  const H = species.matureHeight;

  // The caudex: a fat, water-storing base. This is the whole character of
  // the plant, so it gets the most attention.
  const caudexGeo = d.track(new THREE.SphereGeometry(H * 0.3, 8, 6));
  const caudex = new THREE.Mesh(caudexGeo, stemMat);
  caudex.scale.set(1, 0.72, 1);
  caudex.position.y = H * 0.2;
  caudex.castShadow = true;
  root.add(caudex);

  const arms = new THREE.Group();
  arms.position.y = H * 0.36;
  root.add(arms);

  const armGeo = d.track(new THREE.CylinderGeometry(H * 0.03, H * 0.07, H * 0.36, 6, 1));
  const ARMS = 3;
  const bearings: number[] = [];
  for (let i = 0; i < ARMS; i++) bearings.push((i / ARMS) * Math.PI * 2 + rand() * 0.4);

  // The arms are rigid relative to `arms`, so their pivot rotation folds
  // straight into the instance matrix and no pivot group is needed.
  arms.add(
    d.track(
      instanced(armGeo, stemMat, ARMS, (o, i) => {
        const a = bearings[i];
        o.rotation.set(Math.sin(a) * 0.42, 0, -Math.cos(a) * 0.42);
        o.position.set(0, H * 0.18, 0).applyEuler(o.rotation);
      }),
    ),
  );

  /*
   * The tips do need real groups: each one twists on its own in the wind, and
   * five leaves following one rotation is cheaper as a group transform than
   * as five recomposed matrices per frame. One instanced mesh per tip is
   * three draw calls for what used to be fifteen.
   */
  const tips: THREE.Group[] = [];
  for (let i = 0; i < ARMS; i++) {
    const a = bearings[i];
    const pivot = new THREE.Group();
    pivot.rotation.set(Math.sin(a) * 0.42, 0, -Math.cos(a) * 0.42);

    const tip = new THREE.Group();
    tip.position.y = H * 0.36;
    const leaves = d.track(
      instanced(BLOB_GEO, leafMat, 5, (o, j) => {
        const la = (j / 5) * Math.PI * 2;
        o.position.set(Math.cos(la) * H * 0.07, 0, Math.sin(la) * H * 0.07);
        o.scale.set(H * 0.07, H * 0.022, H * 0.045);
        o.rotation.y = -la;
      }),
    );
    leaves.castShadow = false;
    tip.add(leaves);
    pivot.add(tip);
    arms.add(pivot);
    tips.push(tip);
  }

  const blooms = new THREE.Group();
  const bloomMesh = d.track(
    instanced(BLOOM_GEO, bloomMat, ARMS, (o, i) => {
      const a = (i / ARMS) * Math.PI * 2;
      o.position.set(Math.cos(a) * H * 0.24, H * 0.68, Math.sin(a) * H * 0.24);
      o.scale.setScalar(H * 0.09);
    }),
  );
  bloomMesh.castShadow = false;
  blooms.add(bloomMesh);
  root.add(blooms);

  let growth = 0;

  return {
    root,
    setGrowth(t) {
      growth = THREE.MathUtils.clamp(t, 0, 1);
      // The caudex swells early and keeps swelling — it never stops storing.
      const base = 0.25 + THREE.MathUtils.smoothstep(growth, 0, 1) * 0.75;
      caudex.scale.set(base, base * 0.72, base);
      caudex.position.y = H * 0.2 * base;

      const armT = THREE.MathUtils.smoothstep(growth, 0.3, 0.9);
      arms.position.y = H * 0.36 * base;
      arms.scale.setScalar(0.001 + armT);
      arms.visible = armT > 0.01;

      const flowering = THREE.MathUtils.clamp((growth - 0.7) / 0.3, 0, 1);
      blooms.visible = flowering > 0.01;
      blooms.scale.setScalar(0.001 + flowering);
    },
    crownHeight() {
      return H * 0.7;
    },
    update(time, sway) {
      tips.forEach((tip, i) => {
        tip.rotation.y = Math.sin(time * 0.8 + i) * 0.12 * sway;
      });
      blooms.rotation.y = time * 0.25;
    },
    dispose() {
      d.disposeAll();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Lavender — a low mound with flower spikes
 * ------------------------------------------------------------------ */

function buildShrub(species: Species, seed: number): BuiltPlant {
  const d = new Disposer();
  const root = new THREE.Group();
  const rand = rng(seed);

  const leafMat = d.track(leafMaterial(species.leaf));
  const bloomMat = d.track(bloomMaterial(species.bloom));

  const H = species.matureHeight;

  const mound = new THREE.Group();
  root.add(mound);
  mound.add(
    d.track(
      instanced(BLOB_GEO, leafMat, 9, (o, i) => {
        const a = (i / 9) * Math.PI * 2 + rand();
        const r = i === 0 ? 0 : H * (0.1 + rand() * 0.16);
        o.position.set(Math.cos(a) * r, H * 0.1 + rand() * H * 0.06, Math.sin(a) * r);
        const s = H * (0.11 + rand() * 0.07);
        o.scale.set(s, s * 0.8, s);
        o.rotation.set(rand() * 3, rand() * 3, rand() * 3);
      }),
    ),
  );

  // Flower spikes: the reason anyone plants khuzama.
  const spikes = new THREE.Group();
  root.add(spikes);
  const spikeGeo = d.track(new THREE.ConeGeometry(H * 0.035, H * 0.3, 5));
  const SPIKES = 11;
  const stalks: { x: number; y: number; z: number; lean: number; tilt: number }[] = [];
  for (let i = 0; i < SPIKES; i++) {
    const a = rand() * Math.PI * 2;
    const r = H * rand() * 0.3;
    stalks.push({
      x: Math.cos(a) * r,
      y: H * 0.42 + rand() * H * 0.1,
      z: Math.sin(a) * r,
      lean: (rand() - 0.5) * 0.4,
      tilt: 0,
    });
  }
  const poseSpike = (o: THREE.Object3D, i: number) => {
    const s = stalks[i];
    o.position.set(s.x, s.y, s.z);
    o.rotation.z = s.lean + s.tilt;
  };
  const spikeMesh = d.track(instanced(spikeGeo, bloomMat, SPIKES, poseSpike));
  spikes.add(spikeMesh);

  let growth = 0;

  return {
    root,
    setGrowth(t) {
      growth = THREE.MathUtils.clamp(t, 0, 1);
      const moundT = Math.max(0.06, THREE.MathUtils.smoothstep(growth, 0, 0.75));
      mound.scale.setScalar(moundT);

      const spikeT = THREE.MathUtils.smoothstep(growth, 0.5, 1);
      spikes.scale.set(moundT, 0.001 + spikeT, moundT);
      spikes.visible = spikeT > 0.01;
    },
    crownHeight() {
      return H * 0.55 * Math.max(0.06, THREE.MathUtils.smoothstep(growth, 0, 0.75)) + H * 0.15;
    },
    update(time, sway) {
      stalks.forEach((s, i) => {
        s.tilt = Math.sin(time * 1.6 + i * 0.7) * 0.16 * sway - s.lean;
      });
      repose(spikeMesh, poseSpike);
    },
    dispose() {
      d.disposeAll();
    },
  };
}

/* ------------------------------------------------------------------ */

export function buildPlant(species: Species, seed: number): PlantModel {
  switch (species.form) {
    case 'palm':
      return withMutation(buildPalm(species, seed));
    case 'canopy':
      return withMutation(buildCanopy(species, seed));
    case 'succulent':
      return withMutation(buildSucculent(species, seed));
    case 'shrub':
      return withMutation(buildShrub(species, seed));
  }
}

/**
 * Add mutation tinting to any of the four forms.
 *
 * Done once here rather than in each builder because the operation is
 * identical for all of them and has nothing to do with how a silhouette is
 * assembled. It is safe to mutate the materials in place: every builder
 * constructs its own, so nothing is shared between specimens — tinting one
 * acacia cannot bleach the one in the next bed.
 *
 * Base colours are captured on the first call rather than at build time, so
 * a plant that never mutates costs nothing at all. `InstancedMesh` extends
 * `Mesh`, so the traversal finds the instanced parts without special-casing.
 */
function withMutation(model: BuiltPlant): PlantModel {
  let base: { mat: THREE.MeshStandardMaterial; color: THREE.Color }[] | null = null;

  return {
    ...model,
    setMutation(mutation: Mutation | null) {
      if (!base) {
        const found: { mat: THREE.MeshStandardMaterial; color: THREE.Color }[] = [];
        const seen = new Set<THREE.Material>();
        model.root.traverse((o) => {
          if (!(o instanceof THREE.Mesh)) return;
          const mat = o.material;
          if (!(mat instanceof THREE.MeshStandardMaterial) || seen.has(mat)) return;
          seen.add(mat);
          found.push({ mat, color: mat.color.clone() });
        });
        base = found;
      }

      const tint = mutation ? new THREE.Color(mutation.tint) : null;
      for (const entry of base) {
        entry.mat.color.copy(entry.color);
        if (!mutation || !tint) {
          entry.mat.emissive.setScalar(0);
          continue;
        }
        entry.mat.color.lerp(tint, mutation.mix);
        entry.mat.emissive.copy(tint).multiplyScalar(mutation.glow);
      }
    },
  };
}
